import { Heading } from '@backend/lib-design-system';
import Image from 'next/image';
import { forwardRef, type ReactNode, useMemo } from 'react';
import { styled } from 'styled-components';

import { useTranslation } from '~/hooks';
import { useHref } from '~/hooks/useHref';
import { getMediaQuery } from '~/utils/responsive/media';

import { ListGrid, type ListGridItem } from './ListGrid';

export const ListGridLayoutContainer = styled.div`
  display: flex;
  background-color: white;
  padding: 32px;
  gap: 32px;
  flex-direction: column;
  border-radius: 4px;
`;

export const ListGridLayoutHeader = styled.div`
  align-items: end;
  display: flex;
  gap: 24px;

  ${getMediaQuery('mobile')} {
    align-items: start;
    flex-direction: column;
  }
`;

export const ListGridLayoutIcon = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;

  ${getMediaQuery('mobile')} {
    width: 48px;
    height: 48px;
  }
`;

type ListGridLayoutProps = {
  list: string[];
  itemsPerPage?: number;
  type: 'sectors' | 'cities' | 'jobs';
  footer?: ReactNode;
  maxColumns?: number;
  // the title sits right under the page h1 on a list page, under a section h2 on the home page
  headingLevel?: 'h2' | 'h3';
};

export const ListGridLayout = forwardRef<HTMLDivElement, ListGridLayoutProps>(
  ({ list, itemsPerPage, type, footer, maxColumns, headingLevel = 'h3', ...props }, ref) => {
    const t = useTranslation();
    const { getPageHref } = useHref();

    const listGridItems: ListGridItem[] = useMemo(
      () => list.map(label => ({ label, href: getPageHref(type, label) })),
      [list, getPageHref],
    );

    return (
      <ListGridLayoutContainer {...props} ref={ref}>
        <ListGridLayoutHeader>
          <ListGridLayoutIcon>
            <Image
              src={`/images/icon-${type}.svg`}
              alt=""
              width={60}
              height={60}
              style={{ width: '100%', height: '100%' }}
            />
          </ListGridLayoutIcon>
          <Heading variant="heading-3" as={headingLevel}>
            {t(`list_grid_layout.${type}.title`)}
          </Heading>
        </ListGridLayoutHeader>
        <ListGrid list={listGridItems} itemsPerPage={itemsPerPage} maxColumns={maxColumns} />
        {footer}
      </ListGridLayoutContainer>
    );
  },
);
